<template>
  <div class="admin-assignment">
    <!-- Activity Admin -->
    <div class="mb-3">
      <label class="form-label fw-bold text-sm">
        <i class="material-icons text-primary" style="font-size: 18px; vertical-align: middle">star</i>
        Activity Admin
      </label>
      <p class="text-xs text-secondary mb-2">
        The activity admin manages all workgroups and schedules for this activity.
      </p>
      <div v-if="activityAdmins.length > 0" class="d-flex flex-wrap gap-1 mb-2">
        <span v-for="(admin, idx) in activityAdmins" :key="admin.id"
          class="badge bg-gradient-primary d-flex align-items-center gap-1"
        >
          {{ admin.name }}
          <i class="material-icons" style="font-size: 14px; cursor: pointer" @click="removeActivityAdmin(idx)">close</i>
        </span>
      </div>
      <select class="form-select form-select-sm" v-model="selectedActivityAdmin" @change="addActivityAdmin">
        <option value="">-- Select a user --</option>
        <option v-for="user in availableActivityUsers" :key="user.personId" :value="user.personId">
          {{ user.firstName }} {{ user.lastName }}
        </option>
      </select>
    </div>

    <!-- Workgroup Admins -->
    <div v-for="wg in workgroups" :key="wg.id" class="mb-3 ps-2" style="border-left: 3px solid #e9ecef">
      <label class="form-label fw-bold text-sm">
        <i class="material-icons text-info" style="font-size: 16px; vertical-align: middle">engineering</i>
        {{ wg.name }} Admin
      </label>
      <div v-if="getWgAdmins(wg.id).length > 0" class="d-flex flex-wrap gap-1 mb-2">
        <span v-for="(admin, idx) in getWgAdmins(wg.id)" :key="admin.id"
          class="badge bg-gradient-info d-flex align-items-center gap-1"
        >
          {{ admin.name }}
          <i class="material-icons" style="font-size: 14px; cursor: pointer" @click="removeWgAdmin(wg.id, idx)">close</i>
        </span>
      </div>
      <select class="form-select form-select-sm" :value="''" @change="addWgAdmin(wg.id, $event)">
        <option value="">-- Select a user (optional) --</option>
        <option v-for="user in getAvailableWgUsers(wg.id)" :key="user.personId" :value="user.personId">
          {{ user.firstName }} {{ user.lastName }}
        </option>
      </select>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'

const props = defineProps({
  users: { type: Array, default: () => [] },
  workgroups: { type: Array, default: () => [] },
  currentUserId: { type: [Number, String], default: null },
})

const emit = defineEmits(['update:admins'])

const activityAdmins = ref([])
const workgroupAdmins = ref({})
const selectedActivityAdmin = ref('')

const availableActivityUsers = computed(() => {
  const ids = activityAdmins.value.map(a => String(a.id))
  return props.users.filter(u => !ids.includes(String(u.personId)))
})

function getWgAdmins(wgId) {
  return workgroupAdmins.value[wgId] || []
}

function getAvailableWgUsers(wgId) {
  const ids = (workgroupAdmins.value[wgId] || []).map(a => String(a.id))
  return props.users.filter(u => !ids.includes(String(u.personId)))
}

function addActivityAdmin() {
  if (!selectedActivityAdmin.value) return
  const user = props.users.find(u => String(u.personId) === String(selectedActivityAdmin.value))
  if (user) {
    activityAdmins.value.push({ id: user.personId, name: `${user.firstName} ${user.lastName}` })
    emitAdmins()
  }
  selectedActivityAdmin.value = ''
}

function removeActivityAdmin(idx) {
  activityAdmins.value.splice(idx, 1)
  emitAdmins()
}

function addWgAdmin(wgId, event) {
  const personId = event.target.value
  if (!personId) return
  const user = props.users.find(u => String(u.personId) === String(personId))
  if (user) {
    if (!workgroupAdmins.value[wgId]) workgroupAdmins.value[wgId] = []
    workgroupAdmins.value[wgId].push({ id: user.personId, name: `${user.firstName} ${user.lastName}` })
    workgroupAdmins.value = { ...workgroupAdmins.value }
    emitAdmins()
  }
  event.target.value = ''
}

function removeWgAdmin(wgId, idx) {
  workgroupAdmins.value[wgId].splice(idx, 1)
  workgroupAdmins.value = { ...workgroupAdmins.value }
  emitAdmins()
}

function emitAdmins() {
  emit('update:admins', {
    activityAdmins: activityAdmins.value,
    workgroupAdmins: workgroupAdmins.value,
  })
}

// Pre-select current user as activity admin on mount
onMounted(() => {
  if (props.currentUserId && props.users.length > 0) {
    preSelectCurrentUser()
  }
})

watch(() => props.users, () => {
  if (props.currentUserId && activityAdmins.value.length === 0) {
    preSelectCurrentUser()
  }
})

watch(() => props.workgroups, (wgs) => {
  // Pre-select current user for new workgroups
  if (props.currentUserId && props.users.length > 0) {
    const user = props.users.find(u => String(u.personId) === String(props.currentUserId))
    if (user) {
      for (const wg of wgs) {
        if (!workgroupAdmins.value[wg.id] || workgroupAdmins.value[wg.id].length === 0) {
          workgroupAdmins.value[wg.id] = [{ id: user.personId, name: `${user.firstName} ${user.lastName}` }]
        }
      }
      workgroupAdmins.value = { ...workgroupAdmins.value }
      emitAdmins()
    }
  }
}, { deep: true })

function preSelectCurrentUser() {
  const user = props.users.find(u => String(u.personId) === String(props.currentUserId))
  if (user && activityAdmins.value.length === 0) {
    activityAdmins.value.push({ id: user.personId, name: `${user.firstName} ${user.lastName}` })
    emitAdmins()
  }
}
</script>

<style scoped>
.admin-assignment .badge {
  font-size: 0.75rem;
  padding: 0.35em 0.65em;
}
</style>
